const { v4: uuidv4, validate: validateUuid } = require('uuid');

const ATTRIBUTION_SCHEMA_VERSION = 1;
const TRACE_TTL_DAYS = 90;
const TRACE_TTL_MS = TRACE_TTL_DAYS * 24 * 60 * 60 * 1000;
const TRACE_MAX_BYTES = 256;
const MAX_TELEMETRY_VALUE_LENGTH = 255;
const MAX_LANDING_PATH_LENGTH = 255;
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];
const VALUE_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'];
// Telemetry values may contain normal Unicode and punctuation,
// but not control characters.
// eslint-disable-next-line no-control-regex
const SAFE_VALUE_PATTERN = /^[^\u0000-\u001F\u007F]{1,255}$/u;
const ALLOWED_ENVIRONMENTS = ['production', 'staging', 'development'];

function queryEntries(urlOrSearch, baseUrl) {
  if (urlOrSearch && typeof urlOrSearch === 'object' && !Array.isArray(urlOrSearch)) {
    return Object.entries(urlOrSearch).flatMap(([key, raw]) => {
      const values = Array.isArray(raw) ? raw : [raw];
      return values.map((value) => [key, value]);
    });
  }

  const search = String(urlOrSearch || '');
  const url = search.startsWith('?') ? { search } : new URL(search, baseUrl);
  return [...new URLSearchParams(url.search).entries()];
}

function analyzeUtmParams(urlOrSearch, baseUrl = 'https://glowforge.com/') {
  try {
    const candidates = queryEntries(urlOrSearch, baseUrl).reduce((values, [rawKey, rawValue]) => {
      const key = String(rawKey).toLowerCase();
      if (!UTM_KEYS.includes(key)
          || typeof rawValue !== 'string'
          || !rawValue.trim()) return values;
      values[key].push({ rawKey, value: rawValue.trim() });
      return values;
    }, Object.fromEntries(UTM_KEYS.map((key) => [key, []])));

    const utms = {};
    let keyCaseNormalized = false;
    let keyConflictDetected = false;
    UTM_KEYS.forEach((key) => {
      const matches = candidates[key];
      if (!matches.length) return;
      const canonicalMatch = matches.find(({ rawKey }) => rawKey === key);
      utms[key] = (canonicalMatch || matches[0]).value;
      keyCaseNormalized ||= matches.some(({ rawKey }) => rawKey !== key);
      keyConflictDetected ||= new Set(matches.map(({ value }) => value)).size > 1;
    });

    return {
      utms: Object.keys(utms).length ? utms : null,
      keyCaseNormalized,
      keyConflictDetected,
    };
  } catch {
    return { utms: null, keyCaseNormalized: false, keyConflictDetected: false };
  }
}

function parseUtmParams(urlOrSearch, baseUrl = 'https://glowforge.com/') {
  return analyzeUtmParams(urlOrSearch, baseUrl).utms;
}

function utmKeyMask(utms = {}) {
  return UTM_KEYS.reduce((mask, key, index) => (
    Object.prototype.hasOwnProperty.call(utms, key) ? mask + (2 ** index) : mask
  ), 0);
}

function buildUtmDiagnostics(utms = {}, parsingDiagnostics = {}) {
  const diagnostics = {
    has_utm_source: Boolean(utms.utm_source),
    has_utm_medium: Boolean(utms.utm_medium),
    has_utm_campaign: Boolean(utms.utm_campaign),
    has_utm_content: Boolean(utms.utm_content),
    has_utm_term: Boolean(utms.utm_term),
    utm_key_count: UTM_KEYS.filter((key) => Boolean(utms[key])).length,
  };
  if (typeof parsingDiagnostics.keyCaseNormalized === 'boolean') {
    diagnostics.utm_key_case_normalized = parsingDiagnostics.keyCaseNormalized;
  }
  if (typeof parsingDiagnostics.keyConflictDetected === 'boolean') {
    diagnostics.utm_key_conflict_detected = parsingDiagnostics.keyConflictDetected;
  }

  VALUE_KEYS.forEach((key) => {
    const value = typeof utms[key] === 'string' ? utms[key].trim() : '';
    if (!value) {
      diagnostics[`${key}_value_status`] = 'absent';
    } else if (Array.from(value).length > MAX_TELEMETRY_VALUE_LENGTH) {
      diagnostics[`${key}_value_status`] = 'withheld_too_long';
    } else if (SAFE_VALUE_PATTERN.test(value)) {
      diagnostics[key] = value;
      diagnostics[`${key}_value_status`] = 'included';
    } else {
      diagnostics[`${key}_value_status`] = 'withheld_invalid_format';
    }
  });

  return diagnostics;
}

function createTrace(utms, { now = new Date(), id = uuidv4(), origin = 'browser' } = {}) {
  const trace = {
    v: ATTRIBUTION_SCHEMA_VERSION,
    id,
    at: now.toISOString(),
    mask: utmKeyMask(utms),
    origin,
  };
  // Shared declarations are used by creation and parsing.
  // eslint-disable-next-line no-use-before-define
  if (!validateTrace(trace, now)) throw new Error('Invalid UTM observability trace');
  // eslint-disable-next-line no-use-before-define
  if (byteLength(encodeURIComponent(JSON.stringify(trace))) > TRACE_MAX_BYTES) {
    throw new Error('UTM observability trace exceeds byte budget');
  }
  return trace;
}

function byteLength(value) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).length;
  return unescape(encodeURIComponent(value)).length; // eslint-disable-line no-undef
}

function validateTrace(trace, now = new Date()) {
  if (!trace || trace.v !== ATTRIBUTION_SCHEMA_VERSION || !validateUuid(trace.id)) return false;
  if (!['server', 'browser'].includes(trace.origin)) return false;
  if (!Number.isInteger(trace.mask) || trace.mask < 1 || trace.mask > 31) return false;
  const createdAt = new Date(trace.at).getTime();
  const currentTime = now.getTime();
  return Number.isFinite(createdAt) && createdAt <= currentTime + 60_000
    && currentTime - createdAt <= TRACE_TTL_MS;
}

function parseTrace(raw, now = new Date()) {
  try {
    const trace = JSON.parse(raw);
    return validateTrace(trace, now) ? trace : null;
  } catch {
    return null;
  }
}

function isValidEnvironment(value) {
  return ALLOWED_ENVIRONMENTS.includes(value);
}

function isValidLandingPath(value) {
  return typeof value === 'string'
    && value.startsWith('/')
    && Array.from(value).length <= MAX_LANDING_PATH_LENGTH
    && !value.includes('?')
    && !value.includes('#')
    && SAFE_VALUE_PATTERN.test(value);
}

module.exports = {
  ALLOWED_ENVIRONMENTS,
  ATTRIBUTION_SCHEMA_VERSION,
  MAX_LANDING_PATH_LENGTH,
  MAX_TELEMETRY_VALUE_LENGTH,
  SAFE_VALUE_PATTERN,
  TRACE_MAX_BYTES,
  TRACE_TTL_DAYS,
  TRACE_TTL_MS,
  UTM_KEYS,
  VALUE_KEYS,
  analyzeUtmParams,
  buildUtmDiagnostics,
  byteLength,
  createTrace,
  isValidEnvironment,
  isValidLandingPath,
  parseTrace,
  parseUtmParams,
  utmKeyMask,
  validateTrace,
  validateUuid,
};
